// Spell rules: the inventory (docs/spell-inventory.tsv, compiled into
// data/spells.json — names, levels, durations, shapes, saves, a handler per
// spell) and the rule cards the DM Screen actually runs today. THE GATE: a
// card fires for a party member only when the spell is on their D&D Beyond
// record (Character.spellbook, reported by the live poll) — D&D Beyond's own
// ownership and sharing rules decide what a sheet may know.
import spellsJson from "../data/spells.json";
import { AURAS } from "./auras";
import { LIGHTS } from "./lighting";
import type { Character } from "./types";
import { normSpellName as norm } from "./text";

export type Handler = "aura" | "zone" | "burst-save" | "burst" | "target-condition" | "target-save" | "target-save-timed" | "attack" | "healing" | "buff-timed" | "out-of-scope";
export interface SpellInfo { name: string; level: string; handler: Handler; duration: string; rounds: number | null; concentration: boolean; range: string; aoe?: string; shape?: string; save?: string; effect?: string }
export interface BookSpell { id: number; name: string; level: number; concentration: boolean; ritual: boolean; source: string; prepared: boolean; legacy: boolean }

const INVENTORY = spellsJson as Record<string, SpellInfo>;

export const HANDLER_LABEL: Record<Handler, string> = {
  "aura": "aura", "zone": "placed zone", "burst-save": "area + save", "burst": "area", "target-condition": "save → condition", "target-save": "save", "target-save-timed": "save → lasting effect", "attack": "spell attack", "healing": "healing", "buff-timed": "timed buff", "out-of-scope": "no automation",
};

/** Every spell name the inventory holds (for pickers that list what the DM Screen could run). */
export const inventorySpellNames = (): string[] => Object.values(INVENTORY).map((x) => x.name);
/** What the inventory knows about a spell name (2014 suffixes ignored). */
export const spellInfo = (name: string): SpellInfo | null => INVENTORY[norm(name)] || null;

/** Does the DM Screen run something for this spell today? */
export function ruleCardFor(name: string): { kind: "aura" | "light"; note: string } | null {
  const n = norm(name);
  const aura = AURAS.find((a) => a.match.test(n));
  if (aura) return { kind: "aura", note: `${aura.radius} ft${aura.rounds ? `, ${aura.rounds} rounds` : ""}${aura.dice ? ", dice per turn" : ""}` };
  const light = LIGHTS.find((l) => l.match && l.match.test(n));
  if (light) return { kind: "light", note: `${light.bright}/${light.dim} ft light` };
  return null;
}

/** The gate: may this character's spell drive a handler? */
export function ownsSpell(c: Character, name: string): boolean {
  const n = norm(name);
  return ((c.spellbook as BookSpell[] | undefined) || []).some((s) => norm(s.name) === n);
}

/** The caster on the roster who owns a spell by that name (for a chat card). */
export function casterOf(roster: Character[], casterName: string, spell: string): Character | null {
  const want = norm(casterName);
  const c = roster.find((x) => norm(x.name) === want) || roster.find((x) => want && norm(x.name).split(" ")[0] === want.split(" ")[0]);
  return c && ownsSpell(c, spell) ? c : null;
}

/** The concentration spells on this character's D&D Beyond record, for the
 *  concentration control's suggestions. The record is the gate everywhere
 *  else; here it is only a list to pick from, so a DM can still type a name
 *  the sheet does not carry (an NPC ally's spell, a homebrew effect). */
export function concentrationSpells(c: Character): string[] {
  return ((c.spellbook as BookSpell[] | undefined) || []).filter((s) => s.concentration).map((s) => s.name).sort((a, b) => a.localeCompare(b));
}
